import { type Role } from "./types/role";

// Release Notes for "What's new" (ADR 0005): one Markdown file per change in
// apps/web/content/whats-new/, written for recruiters and admins. Parsed at build time; a broken
// file fails the build.

export const NOTE_KINDS = ["new", "improved", "fixed"] as const;
export const NOTE_AUDIENCES = ["all", "admin"] as const;

export type NoteKind = (typeof NOTE_KINDS)[number];
export type NoteAudience = (typeof NOTE_AUDIENCES)[number];

export interface ReleaseNote {
  id: string; // the file name without .md; what a user's "seen" list stores
  title: string;
  kind: NoteKind;
  audience: NoteAudience;
  date: string; // YYYY-MM-DD, the day the note was written (not the go-live day)
  link: string | null;
  body: string;
}

export interface MonthOfNotes {
  month: string; // YYYY-MM
  notes: ReleaseNote[];
}

const FRONT_MATTER = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
// Links (front matter and body) must be http(s) or an in-app path, never javascript: or //host.
const SAFE_HREF = /^(https?:\/\/|\/(?!\/))/;

// A real calendar day: 2026-02-30 has the right shape but isn't one.
function isRealDay(date: string): boolean {
  const parsed = new Date(`${date}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date;
}

function unquote(value: string): string {
  const trimmed = value.trim();
  return /^(["']).*\1$/.test(trimmed) ? trimmed.slice(1, -1) : trimmed;
}

export function parseNote(fileName: string, source: string): ReleaseNote {
  const fail = (problem: string): never => {
    throw new Error(`Release Note ${fileName}: ${problem}`);
  };
  const match = FRONT_MATTER.exec(source.replace(/\r\n/g, "\n"));
  if (!match) return fail("missing front matter (--- title/kind/audience/date ---)");

  const [, frontMatter = "", rest = ""] = match;
  const fields = new Map<string, string>();
  for (const line of frontMatter.split("\n")) {
    const colon = line.indexOf(":");
    if (colon > 0) fields.set(line.slice(0, colon).trim(), unquote(line.slice(colon + 1)));
  }

  const title = fields.get("title") || fail("missing title");
  const kind = fields.get("kind") ?? "";
  if (!NOTE_KINDS.includes(kind as NoteKind))
    fail(`kind must be one of ${NOTE_KINDS.join(", ")} (got "${kind}")`);
  const audience = fields.get("audience") ?? "";
  if (!NOTE_AUDIENCES.includes(audience as NoteAudience))
    fail(`audience must be one of ${NOTE_AUDIENCES.join(", ")} (got "${audience}")`);
  const date = fields.get("date") ?? "";
  if (!DATE.test(date)) fail(`date must be YYYY-MM-DD (got "${date}")`);
  if (!isRealDay(date)) fail(`date must be a real day (got "${date}")`);
  const link = fields.get("link") || null;
  if (link !== null && !SAFE_HREF.test(link))
    fail(`link must start with http(s):// or / (got "${link}")`);
  const body = rest.trim() || fail("the body is empty; write one or two sentences for users");

  return {
    id: fileName.replace(/\.md$/, ""),
    title,
    kind: kind as NoteKind,
    audience: audience as NoteAudience,
    date,
    link,
    body,
  };
}

// Audience is relevance, not access (ADR 0005): admins also see admin notes.
export function visibleNotes(notes: ReleaseNote[], role: Role): ReleaseNote[] {
  return notes.filter((note) => note.audience === "all" || role === "admin");
}

// Months newest first; within a month, newest note first, then by id so the order is stable.
export function groupByMonth(notes: ReleaseNote[]): MonthOfNotes[] {
  const sorted = [...notes].sort(
    (a, b) => b.date.localeCompare(a.date) || a.id.localeCompare(b.id),
  );
  const months: MonthOfNotes[] = [];
  for (const note of sorted) {
    const month = note.date.slice(0, 7);
    const last = months.at(-1);
    if (last?.month === month) last.notes.push(note);
    else months.push({ month, notes: [note] });
  }
  return months;
}

// A user's first visit counts only notes older than this as already seen, so new users and launch
// day get recent news rather than the whole history, or nothing at all (ADR 0005).
export const FIRST_VISIT_DAYS = 30;

export function initialSeen(notes: ReleaseNote[], today: string): string[] {
  const cutoff = new Date(`${today}T00:00:00Z`);
  cutoff.setUTCDate(cutoff.getUTCDate() - FIRST_VISIT_DAYS);
  const cutoffDay = cutoff.toISOString().slice(0, 10);
  return notes.filter((note) => note.date < cutoffDay).map((note) => note.id);
}

// Seen is a set of ids, never a date watermark: a note written early and released late would
// otherwise count as seen (ADR 0005).
export function hasUnread(notes: ReleaseNote[], seen: string[]): boolean {
  const seenIds = new Set(seen);
  return notes.some((note) => !seenIds.has(note.id));
}

const KIND_ORDER: Record<NoteKind, number> = { new: 0, improved: 1, fixed: 2 };

// The notes a Release Notice announces: visible to this user (audience already applied by
// the caller), not seen, and not announced before. New first, then Improved, then Fixed; newest first.
export function noticeNotes(
  visible: ReleaseNote[],
  seen: string[],
  notified: string[],
): ReleaseNote[] {
  const skip = new Set([...seen, ...notified]);
  return visible
    .filter((note) => !skip.has(note.id))
    .sort(
      (a, b) =>
        KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
        b.date.localeCompare(a.date) ||
        a.id.localeCompare(b.id),
    );
}

// What "notified" starts as, the first time a user has none. A brand-new user (never seen
// anything) gets every current note, so no notice about history; an existing user starts from what they
// have seen, so what's new to them gets one notice.
export function initialNotified(allIds: string[], seenBefore: string[] | null): string[] {
  return seenBefore === null ? [...allIds] : [...seenBefore];
}

// The panel opens on the newest months; older ones wait behind "Frühere Monate" so it stays short.
export const RECENT_MONTHS = 3;

export function splitRecent(months: MonthOfNotes[]): {
  recent: MonthOfNotes[];
  earlier: MonthOfNotes[];
} {
  return { recent: months.slice(0, RECENT_MONTHS), earlier: months.slice(RECENT_MONTHS) };
}

export type InlinePart =
  | { type: "text"; text: string }
  | { type: "bold"; text: string }
  | { type: "link"; text: string; href: string };

const INLINE = /\*\*(.+?)\*\*|\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g;

// A note body allows **bold** and [links](…) only (ADR 0005). Links must be http(s) or an in-app
// path; anything else keeps just its text, so a note can't carry a javascript: URL.
export function inlineMarkdown(body: string): InlinePart[] {
  const parts: InlinePart[] = [];
  const pushText = (text: string) => {
    if (!text) return;
    const last = parts.at(-1);
    if (last?.type === "text") last.text += text;
    else parts.push({ type: "text", text });
  };
  let at = 0;
  for (const match of body.matchAll(INLINE)) {
    pushText(body.slice(at, match.index));
    const [whole, bold, linkText = "", href = ""] = match;
    if (bold !== undefined) parts.push({ type: "bold", text: bold });
    else if (SAFE_HREF.test(href)) parts.push({ type: "link", text: linkText, href });
    else pushText(linkText);
    at = match.index + whole.length;
  }
  pushText(body.slice(at));
  return parts;
}
